import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { Button } from '@/components/ui/button';
import { formatDate } from '../../utils/dateUtils';

const TrainingDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalRecords: 0,
    expiringCerts: 0,
    completedThisMonth: 0
  });
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel
      const [coursesRes, recordsRes, certsRes] = await Promise.all([
        axios.get(`${API}/training-courses`, { headers }),
        axios.get(`${API}/training-records`, { headers }),
        axios.get(`${API}/certifications/expiring?days=30`, { headers })
      ]);

      const courses = coursesRes.data;
      const records = recordsRes.data;
      const certs = certsRes.data;

      // Calculate stats
      const now = new Date();
      const thisMonth = records.filter(r => {
        const date = new Date(r.training_date);
        return date.getMonth() === now.getMonth() && 
               date.getFullYear() === now.getFullYear() &&
               r.completion_status === 'Completed';
      }).length;

      // Exclude Scheduled and In Progress records from Training Records count
      const completedRecords = records.filter(r =>
        r.completion_status !== 'Scheduled' && r.completion_status !== 'In Progress'
      ).length;

      setStats({
        totalCourses: courses.length,
        totalRecords: completedRecords,
        expiringCerts: certs.length,
        completedThisMonth: thisMonth
      });

      setExpiringCerts(certs.slice(0, 5)); // Top 5
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Back to Dashboard */}
        <Button variant="outline" onClick={() => navigate('/safety')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">📚 Training & Competence Management</h1>
          <p className="text-gray-600 mt-1">Manage training courses, records, certifications, and competency requirements</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Courses</p>
                <p className="text-3xl font-bold text-blue-600">{stats.totalCourses}</p>
              </div>
              <div className="text-4xl">📖</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Training Records</p>
                <p className="text-3xl font-bold text-green-600">{stats.totalRecords}</p>
              </div>
              <div className="text-4xl">📋</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Expiring Soon</p>
                <p className="text-3xl font-bold text-orange-600">{stats.expiringCerts}</p>
              </div>
              <div className="text-4xl">⚠️</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed This Month</p>
                <p className="text-3xl font-bold text-purple-600">{stats.completedThisMonth}</p>
              </div>
              <div className="text-4xl">✅</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/training/courses')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 hover:shadow-lg transition-all text-left"
            >
              <div className="text-3xl mb-2">📖</div>
              <h3 className="font-bold text-blue-900">Training Courses</h3>
              <p className="text-sm text-blue-600">Manage course catalog</p>
            </button>

            <button
              onClick={() => navigate('/training/records')}
              className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-lg p-6 hover:shadow-lg transition-all text-left"
            >
              <div className="text-3xl mb-2">📋</div>
              <h3 className="font-bold text-green-900">Training Records</h3>
              <p className="text-sm text-green-600">Track employee training</p>
            </button>

            <button
              onClick={() => navigate('/training/certifications')}
              className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200 rounded-lg p-6 hover:shadow-lg transition-all text-left"
            >
              <div className="text-3xl mb-2">🎓</div>
              <h3 className="font-bold text-orange-900">Certifications</h3>
              <p className="text-sm text-orange-600">Manage licenses & certs</p>
            </button>

            <button
              onClick={() => navigate('/training/competency')}
              className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-lg p-6 hover:shadow-lg transition-all text-left"
            >
              <div className="text-3xl mb-2">📊</div>
              <h3 className="font-bold text-purple-900">Competency Matrix</h3>
              <p className="text-sm text-purple-600">Role requirements</p>
            </button>
          </div>
        </div>

        {/* Expiring Certifications Alert */}
        {expiringCerts.length > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6">
            <h2 className="text-xl font-bold text-yellow-900 mb-4 flex items-center gap-2">
              ⚠️ Certifications Expiring Soon (Next 30 Days)
            </h2>
            <div className="space-y-2">
              {expiringCerts.map(cert => (
                <div key={cert.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                  <div>
                    <p className="font-semibold text-gray-800">{cert.employee_name}</p>
                    <p className="text-sm text-gray-600">{cert.certificate_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-orange-600">
                      Expires: {formatDate(cert.expiry_date)}
                    </p>
                    <p className="text-xs text-gray-500">{cert.issuing_body}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate('/training/certifications?filter=expiring')}
              className="mt-4 text-yellow-700 hover:underline font-semibold"
            >
              View All Expiring Certifications →
            </button>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default TrainingDashboard;